import { isUntimedMode, rotatesBoards, type GameMode } from '../../shared/modes';
import type { EndlessBoard } from '../../shared/protocol';
import { MODE_INFO } from '../content/modes';
import { formatDuration } from './duration';

/** Below this the round timer shows tenths and turns urgent. */
const URGENT_MS = 10_000;

export interface RoundClockInput {
  mode: GameMode;
  dailyDate: string | null;
  /** The round deadline; 0 for untimed rounds. */
  endAt: number;
  endlessBoard: Readonly<EndlessBoard> | null;
  /** Server time. */
  now: number;
}

/** What the HUD's time panel shows and whether the clock still admits words. */
export interface RoundClockView {
  label: string;
  value: string;
  ariaLabel: string;
  urgent: boolean;
  /** False once the round, or the current Endless board, has run out of time. */
  open: boolean;
  /** Whether the displayed values change with time, so the clock must tick. */
  ticking: boolean;
}

/**
 * Timed rounds count down to the round deadline. Endless shows which board is in play and closes
 * play when that board's clock, started by the player's first touch, runs out. Other untimed
 * rounds show no limit.
 */
export function roundClock({ mode, dailyDate, endAt, endlessBoard, now }: RoundClockInput): RoundClockView {
  if (rotatesBoards(mode) && endlessBoard) {
    const running = endlessBoard.endAt > 0;
    const remainingMs = Math.max(0, endlessBoard.endAt - now);
    return {
      label: 'Board',
      value: String(endlessBoard.number),
      ariaLabel: 'Board number',
      urgent: false,
      open: !running || remainingMs > 0,
      // The time left is not shown, but play must close when it runs out.
      ticking: running,
    };
  }
  const title = MODE_INFO[mode].title;
  if (isUntimedMode(mode))
    return {
      label: title,
      value: '∞',
      ariaLabel: 'No time limit',
      urgent: false,
      open: true,
      ticking: false,
    };
  const remainingMs = Math.max(0, endAt - now);
  return {
    label: mode === 'daily' && dailyDate ? `${title} · ${dailyDate} CT` : title,
    value:
      remainingMs < URGENT_MS
        ? (remainingMs / 1000).toFixed(1)
        : formatDuration(Math.ceil(remainingMs / 1000)),
    ariaLabel: 'Time remaining',
    urgent: remainingMs < URGENT_MS,
    open: remainingMs > 0,
    ticking: true,
  };
}
